/**
 * Sign-ups (wave 13, card 3efc00e6): `GET /api/v1/analytics/signups`
 * (`modules/signups`, whose README is the contract), `analytics.read`.
 *
 * How many people signed up each day, how many went on to buy, and which channels
 * brought them. The count comes from the website's synced customers, so every account
 * counts whether or not its visitor allowed analytics; the channel split and the
 * registration events come from measured visits only, and the rest is `unmeasured`
 * rather than hidden: `byChannel` + `unattributed` + `unmeasured` is always the total.
 * The Overview's Sign-ups tile reads the same figures (`/overview/kpis` `signups`) and
 * draws its sparkline from `points`.
 */

import type { GlobalFilters } from '@/lib/filters'

import type { OverviewMetaOut } from './overview'
import type { Envelope } from './types'

export interface SignupsPoint {
  /** A tenant day, `YYYY-MM-DD`. */
  date: string
  signups: number
}

export interface SignupsChannel {
  /** The Traffic page's channel key (`paid_social`, `organic_search`…). */
  channel: string
  /** Its display name, as the API gives it. */
  label: string
  signups: number
}

/** What measured visitors did on the registration form; null before the scope's first event. */
export interface SignupsMeasured {
  /** Measured sessions where the registration form was shown. */
  registrationStarted: number
  /** Measured sessions where an account was created. */
  registrationCompleted: number
  /** Accounts measured as created whose customer has not synced yet: not in the count until it does. */
  pendingSync: number
}

export interface SignupsReport {
  totals: {
    signups: number
    previous: number
    /** The change against the previous period, as a fraction; null when the previous period had none. */
    change: number | null
  }
  /** One per tenant day of the range, oldest first, zero-filled; they sum to `totals.signups`. */
  points: SignupsPoint[]
  /** The previous period, day for day: `previousPoints[i]` compares with `points[i]`. */
  previousPoints: SignupsPoint[]
  converted: {
    /** Sign-ups in the range whose first completed order came at or after registering, before the range ended. */
    count: number
    /** `count ÷ totals.signups`; null with no sign-up. */
    rate: number | null
  }
  /** Sign-ups placed in a channel, most first; only channels with a sign-up. */
  byChannel: SignupsChannel[]
  /** Measured, but their visit has no first-party touch yet (the nightly attribution job places it). */
  unattributed: number
  /** No measured registration: a visitor who declined analytics, an operator-made account, one before tracking. */
  unmeasured: number
  measured: SignupsMeasured | null
  coverage: {
    /** The scope's first behavioural event before the range ended: when tracking started. Null when none. */
    firstEventAt: string | null
    /** The later of the range's start and `firstEventAt`; null when nothing is measured. */
    measuredFrom: string | null
    /** A sentence to show beside the channel split and the event figures. */
    note: string
  }
  /**
   * Sign-ups by the customer's own answer to "Where did you hear about us?": what they
   * told us, neither measured nor estimated, so it is shown apart from `byChannel`. Null
   * when the API does not send it (an older API) or cannot give it.
   */
  bySelfReported: SelfReportedSignups | null
  /** The tenant's timezone, which the days are in. */
  timezone: string
}

/** One answer's sign-ups: this period, its share, the previous period and the change. */
export interface SelfReportedRow {
  /** The answer as the customer gave it; null only for the "No answer" row. */
  answer: string | null
  signups: number
  /** `signups ÷ totals.signups`; null with no sign-up. */
  share: number | null
  previous: number
  /** `signups − previous`: a count, not a percentage. */
  change: number
  /** One per range day, oldest first, zero-filled. */
  points: SignupsPoint[]
}

/** Why the Meta spend per "Facebook ad" sign-up has no figure: `no_spend`, `no_facebook_ad_signups`, `spend_not_convertible`, or a reason a newer API adds. */
export type SelfReportedSpendUnavailable = string

export interface SelfReportedSignups {
  /** Every answer with a sign-up this period or the previous, in the tenant's fixed order (first seen first). */
  rows: SelfReportedRow[]
  /** The sign-ups who gave no answer: always present, never in `rows`. */
  noAnswer: SelfReportedRow
  /** The share of sign-ups who answered; null with no sign-up. */
  answeredShare: number | null
  membersAtStart: number
  membersAtEnd: number
  /** Sign-ups as a percentage of the members at the start (11.4 = 11.4 %); null with no member at the start. */
  memberGrowthPct: number | null
  /** Sign-ups ÷ the previous period's (2.8 = 2.8×); null when the previous period had none. */
  vsPreviousRatio: number | null
  /** The period's Meta spend over the "Facebook ad" row's sign-ups, in minor units of `currency`. */
  spendPerFacebookAdSignup: { spendMinor: number; perSignupMinor: number; currency: string } | null
  /** Why `spendPerFacebookAdSignup` is null; null when it is a figure. */
  spendUnavailable: SelfReportedSpendUnavailable | null
}

export interface SignupsApi {
  signups(filters: GlobalFilters): Promise<Envelope<SignupsReport>>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface SignupsOut {
  data: {
    totals: { signups: number; previous: number; delta_pct: number | null }
    points: SignupsPoint[]
    previous_points: SignupsPoint[]
    converted: { count: number; rate: number | null; window: 'signup_to_range_end' }
    by_channel: SignupsChannel[]
    unattributed: number
    unmeasured: number
    measured: { registration_started: number; registration_completed: number; pending_sync: number } | null
    coverage: { first_event_at: string | null; measured_from: string | null; note: string }
    /** Absent from an API older than card 3f9c00e6-855a-81d4: read as not available. */
    by_self_reported?: SelfReportedOut | null
    not_available: Record<string, string>
  }
  meta: OverviewMetaOut
}

export interface SelfReportedRowOut {
  answer: string | null
  signups: number
  share: number | null
  previous: number
  change: number
  points: SignupsPoint[]
}

export interface SelfReportedOut {
  rows: SelfReportedRowOut[]
  no_answer: SelfReportedRowOut
  answered_share: number | null
  members_at_start: number
  members_at_end: number
  member_growth_pct: number | null
  vs_previous_ratio: number | null
  spend_per_facebook_ad_signup: { spend_minor: number; per_signup_minor: number; currency: string } | null
}

function selfReportedRowFromWire(row: SelfReportedRowOut): SelfReportedRow {
  return {
    answer: row.answer,
    signups: row.signups,
    share: row.share,
    previous: row.previous,
    change: row.change,
    points: row.points.map(({ date, signups }) => ({ date, signups })),
  }
}

/** The self-reported split, or null when the API sent none (an older API, or not available). */
export function selfReportedFromWire(
  wire: SelfReportedOut | null | undefined,
  notAvailable: Record<string, string> | undefined,
): SelfReportedSignups | null {
  if (!wire) return null
  const spend = wire.spend_per_facebook_ad_signup
  return {
    rows: wire.rows.map(selfReportedRowFromWire),
    noAnswer: selfReportedRowFromWire(wire.no_answer),
    answeredShare: wire.answered_share,
    membersAtStart: wire.members_at_start,
    membersAtEnd: wire.members_at_end,
    memberGrowthPct: wire.member_growth_pct,
    vsPreviousRatio: wire.vs_previous_ratio,
    spendPerFacebookAdSignup: spend
      ? { spendMinor: spend.spend_minor, perSignupMinor: spend.per_signup_minor, currency: spend.currency }
      : null,
    spendUnavailable: spend ? null : (notAvailable?.spend_per_facebook_ad_signup ?? 'not_available'),
  }
}

export function signupsFromWire(body: SignupsOut): SignupsReport {
  const { data } = body
  return {
    totals: {
      signups: data.totals.signups,
      previous: data.totals.previous,
      // The API's `delta_pct` is a percentage; the web's change is a fraction.
      change: data.totals.delta_pct === null ? null : data.totals.delta_pct / 100,
    },
    points: data.points.map(({ date, signups }) => ({ date, signups })),
    previousPoints: data.previous_points.map(({ date, signups }) => ({ date, signups })),
    converted: { count: data.converted.count, rate: data.converted.rate },
    byChannel: data.by_channel.map(({ channel, label, signups }) => ({ channel, label, signups })),
    unattributed: data.unattributed,
    unmeasured: data.unmeasured,
    measured: data.measured && {
      registrationStarted: data.measured.registration_started,
      registrationCompleted: data.measured.registration_completed,
      pendingSync: data.measured.pending_sync,
    },
    coverage: {
      firstEventAt: data.coverage.first_event_at,
      measuredFrom: data.coverage.measured_from,
      note: data.coverage.note,
    },
    bySelfReported: selfReportedFromWire(data.by_self_reported, data.not_available),
    timezone: body.meta.timezone,
  }
}
